import { useEffect, useState } from 'react';
import { useCart } from '@/frontastic';
import { combinedKeyPressHandler, getAgentName } from '@/helpers/utils/siteUtils';
import { ADDITIONAL_DETAILS_NAME, ADDITIONAL_DETAILS_TYPE, ADJUSTMENTS } from '../../../../../../constants/constant';
import { ADJUST_ORDER_CONSTANTS } from '../../../order-details/constant';
import { Button } from '../../../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/customSelect/customSelect';
import Loader from '../../../ui/loader';
import { Textarea } from '../../../ui/textarea';
import { minimumAdjustLimit } from '../../constants';

const OrderAdjustments = ({ Texts, noteNumber, pageName = 'cart', className = '' }: any) => {
  const {
    applyShippingDiscount,
    applyAdjustments,
    addAdditionalDetails,
    getAvailableShippingMethods,
    data: cartData,
  } = useCart(false);
  const totalPrice: any = cartData?.subTotal ?? { centAmount: 0 };
  const [error, setError] = useState({ state: false, adj: '', notes: '', ssm: '' });
  const [selectedAdjustment, setSelectedAdjustment] = useState('');
  const [percentCalculation, setPercentCalculation] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (selectedAdjustment === ADJUSTMENTS.PERCENT_OFF && amount) {
      const percentAmount = (totalPrice?.centAmount * parseFloat(amount)) / 100 / 100;
      setPercentCalculation(percentAmount.toFixed(4));
    } else {
      setPercentCalculation('');
    }
  }, [amount]);

  useEffect(() => {
    const adjustAmount =
      selectedAdjustment === ADJUSTMENTS.PERCENT_OFF ? parseFloat(percentCalculation) : parseFloat(amount);
    if (adjustAmount > totalPrice?.centAmount / 100 && totalPrice?.centAmount / 100 < minimumAdjustLimit) {
      setError((prev) => ({ ...prev, state: true, adj: Texts.adjustmentAmountExceed }));
    } else if (adjustAmount > minimumAdjustLimit && totalPrice?.centAmount / 100 > minimumAdjustLimit) {
      setError((prev) => ({ ...prev, state: true, adj: Texts.adjustAmountNotGreaterThan }));
    } else {
      resetError();
    }
  }, [percentCalculation, amount]);

  const handleApplyAdjustment = async () => {
    if (selectedAdjustment === '') {
      setError((prev) => ({ ...prev, state: true, notes: 'Please select an adjustment' }));
      return;
    }
    let isError = false;
    if (!notes) {
      setError((prev) => ({ ...prev, state: true, notes: 'Enter note for this adjustment.' }));
      isError = true;
    }
    if (!amount && (selectedAdjustment === ADJUSTMENTS.DOLLAR_OFF || selectedAdjustment === ADJUSTMENTS.PERCENT_OFF)) {
      setError((prev) => ({ ...prev, state: true, adj: 'Enter a value' }));
      isError = true;
    }
    if (selectedAdjustment !== ADJUSTMENTS.DOLLAR_OFF && selectedAdjustment !== ADJUSTMENTS.PERCENT_OFF) {
      let selectedMethod: any;
      if (cartData?.availableShippingMethods) {
        selectedMethod =
          cartData?.availableShippingMethods?.find(
            (method: any) => method.shippingMethodId === cartData?.selectedShippingMethod,
          ) ?? '';
      } else {
        const availableShippingMethods = await getAvailableShippingMethods();
        selectedMethod =
          availableShippingMethods?.find(
            (method: any) => method.shippingMethodId === cartData?.selectedShippingMethod,
          ) ?? '';
      }
      const selectedShippingDisplayName = selectedMethod?.displayName;
      if (
        selectedAdjustment === 'removeExpedited' &&
        selectedShippingDisplayName !== 'Priority' &&
        selectedShippingDisplayName !== 'Express'
      ) {
        setError((prev) => ({ ...prev, state: true, ssm: 'Shipping Method must be priority or express.' }));
        isError = true;
      }
    }
    if (isError) return;
    setLoading(true);
    if (selectedAdjustment !== ADJUSTMENTS.DOLLAR_OFF && selectedAdjustment !== ADJUSTMENTS.PERCENT_OFF) {
      const payload = {
        shippingDiscountType: selectedAdjustment,
      };
      await applyShippingDiscount(payload);
    } else {
      const payload = {
        discountType: selectedAdjustment,
        amount: parseFloat(amount),
      };
      await applyAdjustments(payload);
    }
    const payload = {
      name: `${ADDITIONAL_DETAILS_NAME.ADD_NOTE}-${noteNumber}`,
      value: notes,
      type: ADDITIONAL_DETAILS_TYPE.NOTES,
      agentName: getAgentName()?.replace(/@.*/, ''),
    };
    await addAdditionalDetails(payload);
    resetState();
    setLoading(false);
  };
  useEffect(() => {
    resetError();
  }, [cartData?.selectedShippingMethod]);

  const resetError = () => {
    setError({ state: false, adj: '', notes: '', ssm: '' });
  };

  const resetState = () => {
    resetError();
    setSelectedAdjustment('');
    setPercentCalculation('');
    setAmount('');
    setNotes('');
  };

  return (
    <Accordion
      type="single"
      collapsible
      className={'w-full ' + className}
      onValueChange={(value) => {
        if (value) setSelectedAdjustment('');
      }}
    >
      <AccordionItem value="openNote">
        <AccordionTrigger className="text-lg font-bold hover:no-underline">
          <p className="mr-auto text-lg font-bold">{Texts.adjustments}</p>
        </AccordionTrigger>
        <AccordionContent className="p-2">
          {loading && <Loader />}
          <section className="mb-2 flex gap-2">
            <section>
              <Select
                onValueChange={(value) => {
                  resetError();
                  setSelectedAdjustment(value);
                }}
                value={selectedAdjustment}
              >
                <SelectTrigger className="w-[400px] border-foggyGray ">
                  <SelectValue placeholder="Select Adjustments" />
                </SelectTrigger>
                <SelectContent className="bg-white ">
                  <SelectGroup>
                    {ADJUST_ORDER_CONSTANTS.ADJUSTMENTS.filter(
                      (adjustment) => pageName !== 'cart' || !['removeExpedited', 'removeAll'].includes(adjustment.key),
                    ).map((adjustment) => (
                      <SelectItem key={`key-${adjustment.key}`} value={adjustment.key}>
                        {adjustment.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </section>
            <section>
              {(selectedAdjustment === ADJUSTMENTS.DOLLAR_OFF || selectedAdjustment === ADJUSTMENTS.PERCENT_OFF) && (
                <section className="flex items-center justify-center gap-2">
                  <div>{selectedAdjustment === ADJUSTMENTS.DOLLAR_OFF && Texts.currency}</div>
                  <input
                    id="numberInput"
                    value={amount}
                    placeholder={selectedAdjustment === ADJUSTMENTS.DOLLAR_OFF ? 'Dollar Off' : 'Percent Off'}
                    className={`w-[160px] rounded-md ${
                      error?.adj
                        ? 'border-scarletRed text-scarletRed placeholder:text-scarletRed'
                        : 'border-foggyGray text-black'
                    }`}
                    maxLength={10}
                    onChange={(e) => setAmount(e.target.value)}
                    type="number"
                  />

                  {selectedAdjustment === ADJUSTMENTS.PERCENT_OFF && (
                    <>
                      <div>{Texts.percent}</div>
                      <section>
                        <input className={`w-[160px] rounded-md p-2 `} disabled value={percentCalculation} />
                      </section>
                    </>
                  )}
                </section>
              )}
            </section>
          </section>
          <section className="flex w-[520px] justify-end">
            {error?.adj ? <section className="mb-2 font-semibold text-scarletRed">{error.adj}</section> : ''}
          </section>
          <section className="flex w-[520px] justify-start">
            {error?.ssm ? <section className="mb-2 font-semibold text-scarletRed">{error.ssm}</section> : ''}
          </section>
          <section>
            <Textarea
              placeholder="Your Notes Here..."
              className={`h-full w-[590px] resize-none border-foggyGray ${
                error?.notes
                  ? 'border-scarletRed text-scarletRed placeholder:text-scarletRed'
                  : 'border-foggyGray text-black'
              }`}
              value={notes}
              onChange={(e) => {
                setError((prev) => ({ ...prev, notes: '' }));
                setNotes(e.target.value);
              }}
              onKeyPress={combinedKeyPressHandler}
              maxLength={120}
            />
          </section>
          {error?.notes ? <section className="mb-2 font-semibold text-scarletRed">{error.notes}</section> : ''}
          <section className="mt-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={handleApplyAdjustment}>
              {Texts.applyAdjustment}
            </Button>
          </section>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default OrderAdjustments;
